import React from 'react'
import { Table, Th, Tr } from 'styling/GlobalStyles'
import { Td } from 'components/Td/Td'
import { NoData } from 'components/NoData'
import {
  AnalyticsTableWrapper,
  RateCell,
  RateBar,
  RateValue,
} from '../HrAnalytics.styled'

export interface ColumnDef {
  key: string
  label: string
  sortable?: boolean
  width?: string
  align?: 'start' | 'center' | 'end'
  renderCell?: (value: any, row: any) => React.ReactNode
}

interface IAnalyticsTable {
  columns: ColumnDef[]
  data: any[]
  minWidth?: string
  emptyText?: string
}

const getRateColor = (value: number): string => {
  if (value >= 90) return '#00B04C'
  if (value >= 70) return '#ff9900'
  return '#FF4F46'
}

export const RateCellRenderer = ({ value }: { value: number }) => {
  const safe = Number(value) || 0
  const isEmpty = safe <= 0
  const color = isEmpty ? '#B0B0B0' : getRateColor(safe)
  return (
    <RateCell>
      <RateBar percent={safe} color={color} />
      <RateValue color={color}>{safe}%</RateValue>
    </RateCell>
  )
}

export const RedValueRenderer = ({ value }: { value: number | string }) => {
  const display = value === null || value === undefined ? 0 : value
  return <span style={{ color: '#FF4F46', fontWeight: 600 }}>{display}</span>
}

const AlignWrapper = ({
  align,
  children,
}: {
  align?: 'start' | 'center' | 'end'
  children: React.ReactNode
}) => (
  <div style={{ width: '100%', display: 'flex', justifyContent: align || 'start' }}>
    {children}
  </div>
)

const renderCellValue = (value: unknown): React.ReactNode => {
  if (value === null || value === undefined || value === '') return 0
  return value as React.ReactNode
}

export const AnalyticsTable = ({ columns, data, minWidth, emptyText }: IAnalyticsTable) => {
  const isEmpty = !data || data.length === 0
  return (
    <AnalyticsTableWrapper>
      <Table>
        <Th minWidth={minWidth || '50rem'}>
          {columns.map((col) => (
            <Td
              key={col.key}
              w={col.width || `${100 / columns.length}%`}
              noTooltip
            >
              <AlignWrapper align={col.align}>{col.label}</AlignWrapper>
            </Td>
          ))}
        </Th>
        {!isEmpty &&
          data.map((row, idx) => (
            <Tr key={idx} minWidth={minWidth || '50rem'}>
              {columns.map((col) => (
                <Td key={col.key} w={col.width || `${100 / columns.length}%`}>
                  <AlignWrapper align={col.align}>
                    {col.renderCell
                      ? col.renderCell(row[col.key], row)
                      : renderCellValue(row[col.key])}
                  </AlignWrapper>
                </Td>
              ))}
            </Tr>
          ))}
      </Table>
      {isEmpty && (
        <div style={{ padding: '2rem 0' }}>
          <NoData text={emptyText} />
        </div>
      )}
    </AnalyticsTableWrapper>
  )
}
